import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import RoleDropdown from "../../components/dropdowns/roleDropdown";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import CustomInput from "../../components/inputs/customInput";
import ImageUpload from "../../components/inputs/uploadImage";
import AddIcon from '@mui/icons-material/Add';
import UserService from "../../services/userService";
import Popup from "../../components/popups/popup";
import { useTheme } from "@emotion/react";
import UserTypeDropdown from "../../components/dropdowns/userTypeDropdown";

const AddUser = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedType, setSelectedType] = useState('');
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedIDImage, setSelectedIDImage] = useState(null);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const [formValues, setFormValues] = useState({
        type: '',
        name: '',
        phone: '',
        phoneTwo: '',
        password: '',
        confirmPassword: '',
        note: '',
        printerName: '',
        roleId: null,
        branchId: null
    });

    const [addErrors, setAddErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleImageChange = (file) => {
        setSelectedImage(file);
    };

    const handleIDImageChange = (file) => {
        setSelectedIDImage(file);
    };

    const validateForm = () => {
        const newAddErrors = {};

        if (!formValues.type) {
            newAddErrors.type = t('userTypeRequired');
        }

        if (!formValues.name) {
            newAddErrors.name = t('nameRequired');
        }

        if (!formValues.phone) {
            newAddErrors.phone = t('phoneRequired');
        }

        if (!formValues.password) {
            newAddErrors.password = t('passwordRequired');
        }

        if (formValues.password.length < 8 && formValues.password) {
            newAddErrors.password = t('passwordMin');
        }


        if (!formValues.confirmPassword) {
            newAddErrors.confirmPassword = t('confirmPasswordRequired');
        }

        if (formValues.password !== formValues.confirmPassword) {
            newAddErrors.confirmPassword = t('confirmPasswordMatch');
        }

        if (!formValues.roleId) {
            newAddErrors.role = t('roleRequired');
        }

        if (!formValues.branchId) {
            newAddErrors.branch = t('branchRequired');
        }

        setAddErrors(newAddErrors);

        return Object.keys(newAddErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const formData = new FormData();

                for (const key in formValues) {
                    if (formValues[key] && formValues[key] !== '')
                        formData.append(key, formValues[key]);
                }

                if (selectedImage) {
                    formData.append('image', selectedImage);
                }

                if (selectedIDImage) {
                    formData.append('IDImage', selectedIDImage);
                }

                const data = await UserService.add(formData);
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    setFormValues({ name: '', phone: '', phoneTwo: '', password: '', confirmPassword: '', note: '', printerName: '' });
                    setSelectedRole(null);
                    setSelectedBranch(null);
                    setSelectedImage(null);
                    setSelectedIDImage(null);
                    setSelectedType('');
                }

                setAddErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setAddErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setAddErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };


    return (
        <Box sx={{
            p: isTablet ? 1 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>

            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    p: isTablet ? 2 : 4
                }}
            >

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Typography id="transition-modal-title" variant="h6" component="h2">
                        {t('addUser')}
                    </Typography>
                </Box>

                {/* row 1 */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    justifyContent: 'space-between'
                }}>
                    {/* role */}
                    <Box width={isTablet ? '100%' : '32%'}>
                        <RoleDropdown
                            helperText={addErrors.role}
                            error={!!addErrors.role}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedRole={selectedRole}
                            setSelectedRole={setSelectedRole}
                            disableSuperAdmin={true}
                        />
                    </Box>

                    {/* Branch */}
                    <Box width={isTablet ? '100%' : '32%'}>
                        <BranchDropdown
                            helperText={addErrors.branch}
                            error={!!addErrors.branch}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedBranch={selectedBranch}
                            setSelectedBranch={setSelectedBranch}
                        />
                    </Box>

                    {/* Type */}
                    <Box width={isTablet ? '100%' : '32%'}>
                        <UserTypeDropdown
                            helperText={addErrors.type}
                            error={!!addErrors.type}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                        />
                    </Box>
                </Box>

                {/* row 2 */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('name')}
                            placeholder={t('nameHere')}
                            name="name"
                            type='text'
                            value={formValues.name}
                            error={!!addErrors.name}
                            helperText={addErrors.name}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('phone')}
                            placeholder={t('phoneHere')}
                            name="phone"
                            type='text'
                            value={formValues.phone}
                            error={!!addErrors.phone}
                            helperText={addErrors.phone}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('phoneTwo')}
                            placeholder={t('phoneTwoHere')}
                            name="phoneTwo"
                            type='text'
                            value={formValues.phoneTwo}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>


                {/* row 3 */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('password')}
                            placeholder="******"
                            name="password"
                            type="password"
                            value={formValues.password}
                            onChange={handleChange}
                            error={!!addErrors.password}
                            helperText={addErrors.password}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('confirmPassword')}
                            placeholder="******"
                            name="confirmPassword"
                            type="password"
                            value={formValues.confirmPassword}
                            onChange={handleChange}
                            error={!!addErrors.confirmPassword}
                            helperText={addErrors.confirmPassword}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('note')}
                            placeholder={t('noteHere')}
                            name="note"
                            type='text'
                            value={formValues.note}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>

                {/* images */}
                <Box
                    sx={{
                        display: isTablet ? 'unset' : 'flex',
                        justifyContent: 'space-between',
                        width: '100%'
                    }}>
                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('printerName')}
                            placeholder={t('printerNameHere')}
                            name="printerName"
                            type='text'
                            value={formValues.printerName}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box sx={{ width: isTablet ? '100%' : '32%' }}>
                        <ImageUpload
                            label={t('uploadImage')}
                            name={t('image')}
                            value={selectedImage}
                            onChange={handleImageChange}
                        />
                    </Box>

                    <Box sx={{ width: isTablet ? '100%' : '32%' }}>
                        <ImageUpload
                            label={t('uploadIDImage')}
                            name={t('IDimage')}
                            value={selectedIDImage}
                            onChange={handleIDImageChange}
                        />
                    </Box>

                </Box>

                {addErrors.general && (
                    <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                        {addErrors.general}
                    </Typography>
                )}

                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mt: addErrors.general ? 2 : 4
                }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                    >
                        {isLoading ? <CircularProgress color="secondary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('add')}</p>
                                <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default AddUser;